import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material'

import { apiClient, authedAxios } from '../utils'
import ListEditor from '../common/ListEditor'
import { EMAIL_REGEX } from '../constants'
import SettingsFooter from './shared/SettingsFooter'
import WizardFooter from '../setupWizard/shared/WizardFooter'
import useAlert from '../hooks/useAlert'
import useRoleTypes from '../hooks/api/useRoleTypes'
import GenericLoadError from '../common/GenericLoadError'

const MAX_ALLOWED_USERS = 12
// MAGIC_NUMBER 3 === 'General user'
const DEFAULT_ROLE_TYPE_ID = 3
const BLANK_USER = {
  emailAddress: '',
  roleTypeId: DEFAULT_ROLE_TYPE_ID,
  firstName: '',
  lastName: '',
}

const Users = ({ wizardFooterProps, state, updateState }) => {
  const hasInitializedData = useMemo(() => !!state.users, [])

  const { createAlert, createGenericErrorAlert } = useAlert()

  const { users = [JSON.parse(JSON.stringify(BLANK_USER))] } = state

  const setUsers = (users) => updateState({ users })

  const [copy, setCopy] = useState(null)
  const [identitiesAreLoading, setIdentitesAreLoading] = useState(true)
  const [duplicateEmails, setDuplicateEmails] = useState([])
  const [reinviteModalOpen, setReniviteModalOpen] = useState(false)
  const [usersToReinvite, setUsersToReinvite] = useState([])
  const [identitiesLoadError, setIdentitesLoadError] = useState(false)

  const { roleTypes, ..._roleTypes } = useRoleTypes()

  const emailAddresses = users.map((u) => u.emailAddress)
  const hasDuplicates = new Set(emailAddresses).size !== emailAddresses.length

  const isLoading = _roleTypes.isLoading || identitiesAreLoading

  const hasLoadError = _roleTypes.hasError || identitiesLoadError

  const checkIfReinviteNeededThenMaybeSubmit = () => {
    const existingUnclaimedUsers = users.filter((u) => u.id && !u.isClaimed)

    if (existingUnclaimedUsers.length > 0) {
      setUsersToReinvite(existingUnclaimedUsers)
      setReniviteModalOpen(true)
      return
    }

    return onSubmit()
  }

  const onSuccess = () =>
    createAlert({
      message: 'Users saved successfully',
      severity: 'success',
    })

  const onFailure = () => createGenericErrorAlert()

  const onSubmit = (options = {}) => {
    const userIdsToReinvite = options?.usersToReinvite || []
    setReniviteModalOpen(false)
    return authedAxios
      .put(
        '/api/identity',
        users.map((u) => ({
          ...u,
          shouldReinvite: userIdsToReinvite.includes(u.id),
        }))
      )
      .then((res) => {
        const { updated, created, newState } = res.data.identities
        const errors = [...updated, ...created].filter((item) => !!item.error)

        const duplicateEmailErrors = errors.filter(
          (e) => e.error.type === 'DUPLICATE_EMAIL'
        )

        if (duplicateEmailErrors.length > 0) {
          setDuplicateEmails(duplicateEmailErrors.map((e) => e.error.value))
          throw new Error('DUPLICATE_EMAIL')
        }

        const data = newState.filter((i) => i.id !== apiClient.user.id)

        setUsers(data)
        setCopy(data)
      })
      .then(() => {
        if (wizardFooterProps) {
          return wizardFooterProps.primaryButton?.onClick?.()
        }

        return onSuccess()
      })
      .catch(onFailure)
  }

  const getIdentities = () => {
    return authedAxios
      .get('/api/identity')
      .then((res) => {
        const identities = res.data.identities.filter(
          (i) => i.id !== apiClient.user.id
        )

        const data = identities.length === 0 ? [BLANK_USER] : identities

        setUsers(data)
        setCopy(data)
        setIdentitesAreLoading(false)
      })
      .catch((e) => {
        setIdentitesAreLoading(false)
        setIdentitesLoadError(true)
      })
  }

  const reset = () => setUsers(copy)

  useEffect(() => {
    if (hasInitializedData) return setIdentitesAreLoading(false)
    getIdentities()
  }, [])

  const getCanEditKey = (key, user) => {
    return !user.isClaimed || (user.isClaimed && key === 'roleTypeId')
  }

  const getCanRemove = (user) => {
    return !user.isClaimed
  }

  const getAltText = (user) => {
    if (user.isClaimed) return 'Claimed'
  }

  const validateUser = ({ emailAddress, roleTypeId }) => {
    return !emailAddress || !EMAIL_REGEX.test(emailAddress) || !roleTypeId
  }

  if (isLoading) return <CircularProgress />

  if (hasLoadError) return <GenericLoadError />

  return (
    <Box>
      <ReinviteModal
        open={reinviteModalOpen}
        onClose={() => setReniviteModalOpen(false)}
        usersToReinvite={usersToReinvite}
        onSubmit={onSubmit}
      ></ReinviteModal>
      <Box>
        <Typography sx={{ mb: 2 }}>
          Here is where you'll add all of the people who will be using this
          system. We'll send them an email to claim their accounts.
        </Typography>
        <Typography>There are three options for roles.</Typography>
        <ul>
          <li style={{ marginBottom: 8 }}>
            <b>Admin:</b> Can see and manage everything in the system. This is
            your role.
          </li>
          <li style={{ marginBottom: 8 }}>
            <b>Manager:</b> Can see other users' activity, but cannot configure
            the system.
          </li>
          <li style={{ marginBottom: 8 }}>
            <b>General User:</b> Can only see their own activity and cannot
            configure the system.
          </li>
        </ul>

        <ListEditor
          list={users}
          editableKeys={[
            { key: 'firstName', label: 'First name' },
            { key: 'lastName', label: 'Last name' },
            { key: 'emailAddress', label: 'Email Address' },
            {
              key: 'roleTypeId',
              label: 'Role',
              options: roleTypes,
              isDropdown: true,
              defaultVal: DEFAULT_ROLE_TYPE_ID,
            },
          ]}
          onChange={(users) => {
            setDuplicateEmails([])
            setUsers(users)
          }}
          getCanEditKey={getCanEditKey}
          getCanRemove={getCanRemove}
          getAltText={getAltText}
          maxLength={MAX_ALLOWED_USERS}
        />

        {duplicateEmails.length > 0 && (
          <Box>
            <Typography color={'error'}>
              Email address{duplicateEmails.length > 1 ? 'es' : ''}{' '}
              {duplicateEmails.map((e, i) => (
                <b>{`${e}${i !== duplicateEmails.length - 1 ? ',' : ''} `}</b>
              ))}
              {duplicateEmails.length > 1 ? 'are' : 'is'} already in use
            </Typography>
          </Box>
        )}
      </Box>

      {wizardFooterProps && (
        <WizardFooter
          {...wizardFooterProps}
          primaryButton={{
            ...wizardFooterProps.primaryButton,
            disabled: users.some((u) => validateUser(u)) || hasDuplicates,
            onClick: () => {
              checkIfReinviteNeededThenMaybeSubmit()
            },
          }}
        />
      )}

      {!wizardFooterProps && (
        <SettingsFooter
          saveButton={{
            disabled: users.some((u) => validateUser(u)) || hasDuplicates,
            onClick: () => {
              checkIfReinviteNeededThenMaybeSubmit()
            },
          }}
          cancelButton={{ onClick: reset }}
        />
      )}
    </Box>
  )
}

export default Users

const ReinviteModal = ({ usersToReinvite, open, onClose, onSubmit }) => {
  const [selectedUserIds, setSelectedUserIds] = useState([])

  const sendInvites = () => {
    onSubmit({ usersToReinvite: selectedUserIds })
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        Resend invites to users who haven't claimed their accounts?
      </DialogTitle>
      <DialogContent>
        <FormGroup>
          {usersToReinvite.map((u) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedUserIds.includes(u.id)}
                  onChange={(_, checked) => {
                    if (checked) {
                      return setSelectedUserIds([
                        ...new Set([...selectedUserIds, u.id]),
                      ])
                    }

                    return setSelectedUserIds(
                      selectedUserIds.filter((id) => id !== u.id)
                    )
                  }}
                />
              }
              label={`${u.firstName} ${u.lastName}`}
            ></FormControlLabel>
          ))}
        </FormGroup>
      </DialogContent>
      <DialogActions>
        <Button variant={'outlined'} onClick={() => onSubmit()}>
          Don't resend invites
        </Button>
        {selectedUserIds.length > 0 && (
          <Button variant={'contained'} onClick={() => sendInvites()}>
            Resend invites
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
